import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, User } from 'firebase/auth';
import React, { useEffect, useRef, useState } from 'react';
import { FaGoogle } from 'react-icons/fa';
import { LuShieldCheck } from 'react-icons/lu';

import { MCP_BASE_URL } from '../shared/api';
import { verifyAuth } from '../shared/api/user';
import { auth } from '../shared/firebase/firebase';

interface McpOAuthParams {
  clientId: string;
  clientName: string;
  redirectUri: string;
  state: string | null;
  codeChallenge: string;
}

// mcp-server redirects here instead of rendering its own (deliberately temporary) consent HTML —
// see mcp-server-contract.md §0. The four required params are passed through unchanged; a
// malformed/missing one means this link didn't come from a real mcp-server /authorize redirect.
function readOAuthParams(): McpOAuthParams | null {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id');
  const redirectUri = params.get('redirect_uri');
  const codeChallenge = params.get('code_challenge');
  if (!clientId || !redirectUri || !codeChallenge) return null;
  try {
    new URL(redirectUri);
  } catch {
    return null;
  }
  return {
    clientId,
    clientName: params.get('client_name') || clientId,
    redirectUri,
    state: params.get('state'),
    codeChallenge,
  };
}

const ConnectMcpPage: React.FC = () => {
  const [oauthParams] = useState(readOAuthParams);
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const idTokenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  const handleGoogle = async () => {
    setError(null);
    setIsSigningIn(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      await verifyAuth();
      setUser(result.user);
    } catch (err) {
      setError((err as Error).message || 'Sign in failed. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSwitchAccount = async () => {
    await auth.signOut();
    setUser(null);
  };

  const handleDeny = () => {
    if (!oauthParams) return;
    const url = new URL(oauthParams.redirectUri);
    url.searchParams.set('error', 'access_denied');
    if (oauthParams.state) url.searchParams.set('state', oauthParams.state);
    window.location.href = url.toString();
  };

  // A real HTML form POST + full-page navigation, not fetch — mcp-server's /authorize/complete
  // responds with an HTTP redirect back to the MCP client's redirect_uri, which only a genuine
  // navigation follows. See mcp-server-contract.md §0 step 3.
  const handleApprove = async () => {
    if (!oauthParams || !user || !formRef.current || !idTokenInputRef.current) return;
    setError(null);
    setIsApproving(true);
    try {
      idTokenInputRef.current.value = await user.getIdToken();
      formRef.current.submit();
    } catch (err) {
      setError((err as Error).message || 'Failed to approve. Please try again.');
      setIsApproving(false);
    }
  };

  if (!oauthParams) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200 p-6">
        <div className="alert alert-error max-w-sm">
          <span>This link is missing required parameters. Please restart the connection from your MCP client.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-6">
      <div className="w-full max-w-sm rounded-xl bg-base-100 shadow-panel p-6 space-y-5">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="mFlow" className="h-8 w-8" />
          <span className="font-display font-bold text-lg">MFlow</span>
        </div>

        {!authChecked ? (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-lg opacity-40" />
          </div>
        ) : !user ? (
          <>
            <div className="space-y-1">
              <h1 className="text-xl font-semibold">Sign in to connect {oauthParams.clientName}</h1>
              <p className="text-sm opacity-60">Sign in to your mFlow account to continue.</p>
            </div>
            <button onClick={handleGoogle} disabled={isSigningIn} className="btn btn-outline w-full">
              <FaGoogle className="mr-1" />
              {isSigningIn ? 'Signing in…' : 'Sign in with Google'}
            </button>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <LuShieldCheck className="text-primary text-2xl" />
              <h1 className="text-xl font-semibold">
                Allow <span className="text-primary">{oauthParams.clientName}</span> to access your mFlow account?
              </h1>
              <p className="text-sm opacity-60">
                Signed in as <b>{user.email ?? user.uid}</b>.{' '}
                <button type="button" className="link link-primary" onClick={handleSwitchAccount}>
                  Not you?
                </button>
              </p>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-ghost flex-1" onClick={handleDeny} disabled={isApproving}>
                Deny
              </button>
              <button className="btn btn-primary flex-1" onClick={handleApprove} disabled={isApproving}>
                {isApproving && <span className="loading loading-spinner loading-xs" />}
                Allow access
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form ref={formRef} method="POST" action={`${MCP_BASE_URL}/authorize/complete`} className="hidden">
          <input ref={idTokenInputRef} type="hidden" name="idToken" />
          <input type="hidden" name="clientId" value={oauthParams.clientId} />
          <input type="hidden" name="redirectUri" value={oauthParams.redirectUri} />
          <input type="hidden" name="codeChallenge" value={oauthParams.codeChallenge} />
          {oauthParams.state && <input type="hidden" name="state" value={oauthParams.state} />}
        </form>
      </div>
    </div>
  );
};

export default ConnectMcpPage;
